/**
 * 专享算力相关的 TanStack Query Hooks
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import {
  getExclusiveSettings,
  updateExclusiveSettings,
  getExclusiveServers,
  startExclusiveServers,
  startExclusiveServer,
  stopExclusiveServer,
} from '@/services/exclusiveComputeService'
import type {
  ExclusiveComputeSettings,
  ExclusiveComputeSettingsUpdate,
  ExclusiveComputeServerList,
  ExclusiveComputeActionResponse,
  ExclusiveComputeBatchActionResponse,
  ExclusiveStartMode,
} from '@/types/exclusiveCompute'

/** 专享设置查询 Key */
const EXCLUSIVE_SETTINGS_QUERY_KEY = ['exclusive', 'settings'] as const

/** 专享服务器查询 Key */
const EXCLUSIVE_SERVERS_QUERY_KEY = ['exclusive', 'servers'] as const

/**
 * 获取专享算力配置
 * @returns 查询结果
 */
export function useExclusiveSettings(
  enabled = true
): UseQueryResult<ExclusiveComputeSettings> {
  return useQuery({
    queryKey: EXCLUSIVE_SETTINGS_QUERY_KEY,
    queryFn: async (): Promise<ExclusiveComputeSettings> => {
      return await getExclusiveSettings()
    },
    enabled,
  })
}

/**
 * 获取专享服务器列表
 * @returns 查询结果
 */
export function useExclusiveServers(
  enabled = true
): UseQueryResult<ExclusiveComputeServerList> {
  return useQuery({
    queryKey: EXCLUSIVE_SERVERS_QUERY_KEY,
    queryFn: async (): Promise<ExclusiveComputeServerList> => {
      return await getExclusiveServers()
    },
    enabled,
  })
}

/**
 * 更新专享算力配置
 * @returns 变更结果
 */
export function useUpdateExclusiveSettings(): UseMutationResult<
  ExclusiveComputeSettings,
  Error,
  ExclusiveComputeSettingsUpdate
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ExclusiveComputeSettingsUpdate): Promise<ExclusiveComputeSettings> => {
      return await updateExclusiveSettings(payload)
    },
    onSuccess: async (): Promise<void> => {
      await queryClient.invalidateQueries({ queryKey: EXCLUSIVE_SETTINGS_QUERY_KEY })
    },
  })
}

/**
 * 批量启动专享服务器
 * @returns 变更结果
 */
export function useStartExclusiveServers(): UseMutationResult<
  ExclusiveComputeBatchActionResponse,
  Error,
  { count: number, mode: ExclusiveStartMode }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { count: number, mode: ExclusiveStartMode }): Promise<ExclusiveComputeBatchActionResponse> => {
      return await startExclusiveServers(payload.count, payload.mode)
    },
    onSuccess: async (): Promise<void> => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: EXCLUSIVE_SERVERS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: EXCLUSIVE_SETTINGS_QUERY_KEY }),
      ])
    },
  })
}

/**
 * 启动专享服务器
 * @returns 变更结果
 */
export function useStartExclusiveServer(): UseMutationResult<
  ExclusiveComputeActionResponse,
  Error,
  string
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (serverId: string): Promise<ExclusiveComputeActionResponse> => {
      return await startExclusiveServer(serverId)
    },
    onSuccess: async (): Promise<void> => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: EXCLUSIVE_SERVERS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: EXCLUSIVE_SETTINGS_QUERY_KEY }),
      ])
    },
  })
}

/**
 * 关闭专享服务器
 * @returns 变更结果
 */
export function useStopExclusiveServer(): UseMutationResult<
  ExclusiveComputeActionResponse,
  Error,
  string
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (serverId: string): Promise<ExclusiveComputeActionResponse> => {
      return await stopExclusiveServer(serverId)
    },
    onSuccess: async (): Promise<void> => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: EXCLUSIVE_SERVERS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: EXCLUSIVE_SETTINGS_QUERY_KEY }),
      ])
    },
  })
}

export {
  EXCLUSIVE_SETTINGS_QUERY_KEY,
  EXCLUSIVE_SERVERS_QUERY_KEY,
}
